/**
 * Version State Module
 *
 * Provides module-level version state that can be accessed outside of React hooks.
 * This is critical for the lazyWithReload function in routes.tsx which needs to
 * check for version mismatches BEFORE attempting to load lazy chunks.
 *
 * The problem: React hooks can only be called from React components/hooks,
 * but lazyWithReload runs during route resolution (outside React lifecycle).
 * This module bridges that gap with a simple module-level state store.
 *
 * Flow:
 * 1. App loads -> main.tsx calls initializeVersionState()
 * 2. Version is fetched and stored in module state
 * 3. On navigation, lazyWithReload() calls checkVersionBeforeImport()
 * 4. If mismatch detected, reload happens BEFORE chunk load attempt
 * 5. useVersionCheck hook syncs with this module for React components
 */

import { logger } from './logger';

/**
 * Version information structure from version.json
 */
export interface VersionInfo {
  timestamp: string;
  buildId: string;
  buildDate: string;
  /** Semantic app version from package.json (present in version.json) */
  version?: string;
}

/**
 * Module-level state (singleton pattern)
 * This persists across the app lifecycle and is accessible from anywhere
 */
interface VersionState {
  /** The buildId that was loaded with the current main.js bundle */
  initialBuildId: string | null;
  /** Cached version info from most recent server check */
  cachedVersion: VersionInfo | null;
  /** Timestamp of last version check */
  lastCheckTime: number;
  /** Whether initialization has completed */
  initialized: boolean;
  /** Flag to prevent concurrent fetches */
  isFetching: boolean;
}

const state: VersionState = {
  initialBuildId: null,
  cachedVersion: null,
  lastCheckTime: 0,
  initialized: false,
  isFetching: false,
};

/** Cache duration for quick checks (15 seconds) */
const QUICK_CHECK_CACHE_MS = 15000;

/** Minimum time between fetches to prevent hammering (5 seconds) */
const MIN_FETCH_INTERVAL_MS = 5000;

/**
 * Fetch version.json from server with cache-busting
 * This is a lightweight fetch without retry logic for quick checks
 */
async function fetchVersionQuick(): Promise<VersionInfo | null> {
  try {
    const cacheBuster = Date.now();
    const response = await fetch(`/static/dist/version.json?_=${cacheBuster}`, {
      // Short timeout for quick checks
      signal: AbortSignal.timeout(5000),
    });

    if (!response.ok) {
      if (response.status === 404) {
        logger.warn('[VersionState] version.json not found (404)');
      }
      return null;
    }

    return await response.json();
  } catch (error) {
    // Network errors, timeouts, etc. - fail silently for quick checks
    logger.warn('[VersionState] Quick version fetch failed:', error);
    return null;
  }
}

/**
 * Fetch version.json with retry logic for initial load
 * More robust than quick fetch - used during app initialization
 */
async function fetchVersionWithRetry(): Promise<VersionInfo | null> {
  const MAX_RETRIES = 3;
  const BASE_DELAY = 1000;

  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    try {
      const cacheBuster = Date.now();
      const response = await fetch(`/static/dist/version.json?_=${cacheBuster}`);

      if (!response.ok) {
        if (response.status === 404) {
          console.error('[VersionState] version.json not found (404)');
          return null;
        }
        throw new Error(`HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      const isLastAttempt = attempt === MAX_RETRIES - 1;

      if (isLastAttempt) {
        console.error('[VersionState] Failed to fetch version after retries:', error);
        return null;
      }

      // Exponential backoff
      const delay = BASE_DELAY * Math.pow(2, attempt);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  return null;
}

/**
 * Initialize version state at app startup
 *
 * Call this as early as possible in main.tsx, before React renders.
 * This establishes the "initial" buildId that we'll compare against
 * to detect if a new deployment has occurred.
 *
 * @returns Promise that resolves when initialization is complete
 */
export async function initializeVersionState(): Promise<void> {
  if (state.initialized) {
    return;
  }

  // Check localStorage first for faster startup
  const storedVersion = localStorage.getItem('app_version');
  if (storedVersion) {
    try {
      const parsed: VersionInfo = JSON.parse(storedVersion);
      state.initialBuildId = parsed.buildId;
      state.cachedVersion = parsed;
      state.lastCheckTime = Date.now();
    } catch {
      // Invalid stored version, will fetch fresh
    }
  }

  // Fetch current version from server
  const serverVersion = await fetchVersionWithRetry();

  if (serverVersion) {
    // If we didn't have a stored version, or it matches server, use server version
    if (!state.initialBuildId || state.initialBuildId === serverVersion.buildId) {
      state.initialBuildId = serverVersion.buildId;
      state.cachedVersion = serverVersion;
      state.lastCheckTime = Date.now();
      localStorage.setItem('app_version', JSON.stringify(serverVersion));
    } else {
      // We have a stored version that differs from server!
      // This means we loaded with old main.js but server has new version
      // Store the server version but keep initialBuildId as what we loaded with
      // The mismatch will be detected on first navigation
      state.cachedVersion = serverVersion;
      state.lastCheckTime = Date.now();
      localStorage.setItem('app_version', JSON.stringify(serverVersion));
      logger.warn(
        '[VersionState] Version mismatch detected on init:',
        `loaded=${state.initialBuildId}, server=${serverVersion.buildId}`
      );
    }
  }

  state.initialized = true;
  logger.log('[VersionState] Initialized with buildId:', state.initialBuildId);
}

/**
 * Check if there's a version mismatch before attempting a lazy import
 *
 * This is the key function that prevents stale chunk errors.
 * Called by lazyWithReload() in routes.tsx BEFORE attempting import.
 *
 * Logic:
 * 1. If we already know there's a mismatch (cached version differs from initial), return true
 * 2. If cache is fresh (<15s), use cached result
 * 3. If cache is stale, do a quick async fetch and check
 *
 * @returns Promise<boolean> - true if version mismatch detected (should reload)
 */
export async function checkVersionBeforeImport(): Promise<boolean> {
  // If not initialized, can't check - allow import to proceed
  if (!state.initialized || !state.initialBuildId) {
    return false;
  }

  const now = Date.now();

  // Check 1: Do we already know there's a mismatch?
  if (state.cachedVersion && state.cachedVersion.buildId !== state.initialBuildId) {
    logger.log(
      '[VersionState] Known version mismatch:',
      `initial=${state.initialBuildId}, cached=${state.cachedVersion.buildId}`
    );
    return true;
  }

  // Check 2: Is our cache fresh enough?
  const cacheAge = now - state.lastCheckTime;
  if (cacheAge < QUICK_CHECK_CACHE_MS) {
    // Cache is fresh and shows no mismatch
    return false;
  }

  // Check 3: Cache is stale - need to fetch fresh version
  // But don't fetch too frequently
  if (state.isFetching || cacheAge < MIN_FETCH_INTERVAL_MS) {
    // Either already fetching or fetched very recently
    return false;
  }

  state.isFetching = true;

  try {
    const serverVersion = await fetchVersionQuick();

    if (serverVersion) {
      state.cachedVersion = serverVersion;
      state.lastCheckTime = Date.now();

      // Check for mismatch
      if (serverVersion.buildId !== state.initialBuildId) {
        logger.log(
          '[VersionState] Fresh check found version mismatch:',
          `initial=${state.initialBuildId}, server=${serverVersion.buildId}`
        );
        localStorage.setItem('app_version', JSON.stringify(serverVersion));
        return true;
      }
    }

    return false;
  } finally {
    state.isFetching = false;
  }
}

/**
 * Get the current cached version info
 * Used by useVersionCheck hook to sync with module state
 */
export function getCachedVersion(): VersionInfo | null {
  return state.cachedVersion;
}

/**
 * Get the initial buildId that was loaded with this main.js
 */
export function getInitialBuildId(): string | null {
  return state.initialBuildId;
}

/**
 * Get the timestamp of the last version check
 */
export function getLastCheckTime(): number {
  return state.lastCheckTime;
}

/**
 * Check if version state is initialized
 */
export function isVersionStateInitialized(): boolean {
  return state.initialized;
}

/**
 * Update the cached version (called by useVersionCheck when it fetches)
 * This keeps the module state in sync with the hook's fetches
 */
export function updateCachedVersion(version: VersionInfo): void {
  state.cachedVersion = version;
  state.lastCheckTime = Date.now();
}

/**
 * Force a version check (bypass cache)
 * Returns true if mismatch detected
 */
export async function forceVersionCheck(): Promise<boolean> {
  if (!state.initialBuildId) {
    return false;
  }

  const serverVersion = await fetchVersionWithRetry();

  if (serverVersion) {
    state.cachedVersion = serverVersion;
    state.lastCheckTime = Date.now();
    localStorage.setItem('app_version', JSON.stringify(serverVersion));

    return serverVersion.buildId !== state.initialBuildId;
  }

  return false;
}
